import { PostStatus } from '../../data-layer/posts/constants';
import type { PostModel } from '../../data-layer/posts/types';
import type { JsonLdObject } from '../types';

const MAX_MEDIA_NAME_LENGTH = 120;
const ISO_8601_DATE_TIME =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;

interface PostMediaDescription {
    id: string;
    text: string;
    content?: ReadonlyArray<{ type: 'image' | 'video' }>;
    business?: {
        displayName?: string;
    };
}

const normalizeText = (text: string): string => text.replace(/\s+/g, ' ').trim();

const truncateMediaName = (name: string): string => {
    if (name.length <= MAX_MEDIA_NAME_LENGTH) {
        return name;
    }

    return `${name.slice(0, MAX_MEDIA_NAME_LENGTH - 1).trimEnd()}…`;
};

export const buildPostMediaName = (post: PostMediaDescription): string => {
    const text = normalizeText(post.text);

    if (text) {
        return truncateMediaName(text);
    }

    const mediaName = post.content?.[0]?.type === 'video' ? 'Видео' : 'Изображение';
    const businessName = normalizeText(post.business?.displayName ?? '');
    const fallback = businessName
        ? `${mediaName} публикации «${businessName}»`
        : `${mediaName} публикации в Яндекс Ритме`;

    return truncateMediaName(fallback);
};

const getHttpsUrl = (value?: string): string | undefined => {
    if (!value) {
        return undefined;
    }

    try {
        const url = new URL(value);

        if (url.protocol !== 'https:' || url.username || url.password) {
            return undefined;
        }

        return url.toString();
    } catch {
        return undefined;
    }
};

const getUploadDate = (value: string): string | undefined =>
    ISO_8601_DATE_TIME.test(value) && !Number.isNaN(Date.parse(value)) ? value : undefined;

const getPositiveDimension = (value: number): number | undefined =>
    Number.isFinite(value) && value > 0 ? value : undefined;

const getDuration = (durationMs?: number): string | undefined => {
    if (!durationMs || !Number.isFinite(durationMs) || durationMs <= 0) {
        return undefined;
    }

    const roundedDurationMs = Math.round(durationMs);

    return roundedDurationMs > 0 ? `PT${roundedDurationMs / 1000}S` : undefined;
};

const getLargestHttpsImage = (images: Inspire.Image[]): Inspire.Image | undefined =>
    images
        .filter((image) => getHttpsUrl(image.url))
        .reduce<Inspire.Image | undefined>((largest, image) => {
            if (!largest || image.width * image.height > largest.width * largest.height) {
                return image;
            }

            return largest;
        }, undefined);

const getVideoThumbnailUrl = (video?: Inspire.Video): string | undefined => {
    const firstFrameUrl = getHttpsUrl(video?.firstFrameUrl);

    if (firstFrameUrl && !new URL(firstFrameUrl).pathname.endsWith('/images/firstFrame.png')) {
        return firstFrameUrl;
    }

    return getHttpsUrl(getLargestHttpsImage(video?.previewFormats ?? [])?.url);
};

export const buildPostMediaJsonLd = (
    post: PostModel | undefined,
    canonicalUrl: string,
): JsonLdObject | undefined => {
    const httpsCanonicalUrl = getHttpsUrl(canonicalUrl);

    if (!post || post.status !== PostStatus.PUBLISHED || !httpsCanonicalUrl) {
        return undefined;
    }

    const [content] = post.content;

    if (!content) {
        return undefined;
    }

    const name = buildPostMediaName(post);
    const description = normalizeText(post.text) || name;
    const commonData = {
        '@context': 'https://schema.org',
        url: httpsCanonicalUrl,
        name,
    } as const;

    if (content.type === 'image') {
        const image = getLargestHttpsImage(content.media);
        const contentUrl = getHttpsUrl(image?.url);

        if (!image || !contentUrl) {
            return undefined;
        }

        return {
            ...commonData,
            '@type': 'ImageObject',
            contentUrl,
            caption: description,
            ...(getPositiveDimension(image.width) ? { width: image.width } : {}),
            ...(getPositiveDimension(image.height) ? { height: image.height } : {}),
        };
    }

    const [video] = content.media;
    const contentUrl = getHttpsUrl(video?.url);
    const thumbnailUrl = getVideoThumbnailUrl(video);
    const uploadDate = getUploadDate(post.createdAt);
    const duration = getDuration(video?.durationMs);

    if (!video || !contentUrl || !thumbnailUrl || !uploadDate) {
        return undefined;
    }

    return {
        ...commonData,
        '@type': 'VideoObject',
        description,
        thumbnailUrl,
        uploadDate,
        contentUrl,
        ...(duration ? { duration } : {}),
        ...(getPositiveDimension(video.width) ? { width: video.width } : {}),
        ...(getPositiveDimension(video.height) ? { height: video.height } : {}),
    };
};
